import { yupResolver } from "@hookform/resolvers/yup";
import { assignSlotToApplicantsApi, sendJobDriveInvitationsApi } from "api/services/jobs";
import { Button } from "components/Button";
import EssFullWidthModal from "components/modelpopup/baseModal";
import { format, parseISO } from "date-fns";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import { Bounce, toast } from "react-toastify";
import { customStyleSelect } from "utils/common";
import * as Yup from "yup";

const AssignSlotModal = ({
  open,
  onCancel,
  jobId,
  applicant,
  slots,
  onSuccess,
  loading
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [sendInvitation, setSendInvitation] = useState(false);

  const slotSchema = Yup.object().shape({
    slot_id: Yup.object().required("Please select a time slot"),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(slotSchema),
    defaultValues: {
      slot_id: null,
    },
  });

  useEffect(() => {
    // Check if applicant already has an invitation with a slot
    if (open) {
      reset();
    }
  }, [applicant, open, setValue, reset]);

  const slotOptions = slots?.map(slot => {
    if (slot.start_at && slot.end_at) {
      const startDate = parseISO(slot.start_at);
      const endDate = parseISO(slot.end_at);
      const label = `${slot.title || "Slot"} - ${format(startDate, "dd-MM-yyyy HH:mm")} - ${format(endDate, "HH:mm")} (${slot.invited_count || 0}/${slot.capacity} candidates)`;
      return {
        label,
        value: slot.id,
        disabled: (slot.invited_count || 0) >= slot.capacity,
      };
    }
    return {
      label: slot.title || `Slot ${slot.id}`,
      value: slot.id,
      disabled: (slot.invited_count || 0) >= slot.capacity,
    };
  }) || [];

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      // Assign slot to applicant(s) - API expects array of applicant IDs
      await assignSlotToApplicantsApi([applicant.id], data.slot_id.value);
      
      if (sendInvitation) {
        // Send invitation - API expects array of applicant IDs and slot ID
        const response = await sendJobDriveInvitationsApi([applicant.id], data.slot_id.value);
        
        if (response.data?.error) {
          throw new Error(response.data.error);
        }
        
        toast.success("Slot assigned and invitation sent successfully!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      } else {
        toast.success("Slot assigned successfully!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      }
      
      reset();
      setSendInvitation(false);
      if (onSuccess) onSuccess();
      onCancel();
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.response?.data?.detail || error.message || "Failed to assign slot";
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setSendInvitation(false);
    onCancel();
  };

  return (
    <EssFullWidthModal
      mask={false}
      title="Assign Time Slot"
      open={open}
      width="40%"
      footer={[]}
      onCancel={handleClose}
      className="full-width-modal"
    >
      <div className="modal-content">
        <div className="modal-body">
          {applicant && (
            <div className="mb-3 p-3" style={{ background: "#f8f9fa", borderRadius: "8px" }}>
              <p className="mb-1"><strong>Applicant:</strong> {applicant.first_name} {applicant.last_name}</p>
              <p className="mb-0"><strong>Email:</strong> {applicant.email}</p>
            </div>
          )}
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="row">
              <div className="col-md-12">
                <div className="input-block mb-3">
                  <label className="col-form-label">Select Time Slot <span className="text-danger">*</span></label>
                  <Controller
                    name="slot_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        placeholder="Select a time slot"
                        onChange={field.onChange}
                        value={field.value}
                        options={slotOptions}
                        styles={customStyleSelect}
                        isDisabled={isLoading || loading}
                      />
                    )}
                  />
                  {errors.slot_id && (
                    <small className="text-danger">{errors.slot_id?.message}</small>
                  )}
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-12">
                <div className="input-block mb-3">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="send_invitation"
                      checked={sendInvitation}
                      onChange={(e) => setSendInvitation(e.target.checked)}
                      style={{ cursor: "pointer" }}
                    />
                    <label className="form-check-label" htmlFor="send_invitation" style={{ cursor: "pointer" }}>
                      Send invitation email with QR code immediately
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="submit-section">
              <Button
                loading={isLoading || loading}
                disabled={isLoading || loading}
                className="btn btn-primary submit-btn"
                type="submit"
                label="Assign Slot"
              />
            </div>
          </form>
        </div>
      </div>
    </EssFullWidthModal>
  );
};

export default AssignSlotModal;

